import React from "react";
import { SvgIcon } from "@mui/material";

// Material UI icons
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import BarChartIcon from "@mui/icons-material/BarChart";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PublicIcon from "@mui/icons-material/Public";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import PaymentIcon from "@mui/icons-material/Payment";
import GroupIcon from "@mui/icons-material/Group";
import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";
import { AssignmentTurnedIn } from "@mui/icons-material";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import DescriptionIcon from "@mui/icons-material/Description";
import BadgeIcon from "@mui/icons-material/Badge";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import SettingsIcon from "@mui/icons-material/Settings";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import EngineeringIcon from "@mui/icons-material/Engineering";
import AssessmentIcon from "@mui/icons-material/Assessment";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import BuildIcon from "@mui/icons-material/Build";
import ViewKanbanIcon from "@mui/icons-material/ViewKanban";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ChatIcon from "@mui/icons-material/Chat";
import NotificationsIcon from "@mui/icons-material/Notifications";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import ImageIcon from "@mui/icons-material/Image";
import EditIcon from "@mui/icons-material/Edit";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import EmailIcon from "@mui/icons-material/Email";

// react-icons
import { MdUploadFile } from "react-icons/md";
import { PiStampLight } from "react-icons/pi";

// heroicons (import as a named import and rename to avoid name clash)
import { ChartBarIcon as HeroChartBarIcon } from "@heroicons/react/24/solid";

import { isPermitRockstarDomain } from "../../utils/domainUtils";

/**
 * Notary-specific menu items
 */
export const notaryItems = [
  {
    title: "Dashboard",
    path: "/notary/dashboard",
    icon: (
      <SvgIcon fontSize="small">
        <BarChartIcon />
      </SvgIcon>
    ),
  },
  {
    title: "Job Management",
    path: "/notary/history",
    icon: (
      <SvgIcon fontSize="small">
        <WorkOutlineIcon />
      </SvgIcon>
    ),
  },
  {
    title: "IPEN",
    path: "/notary/ipen",
    icon: (
      <SvgIcon fontSize="small">
        <BadgeIcon />
      </SvgIcon>
    ),
  },
  {
    title: "My Organization",
    path: "/notary/organization",
    icon: (
      <SvgIcon fontSize="small">
        <BusinessIcon />
      </SvgIcon>
    ),
  },

  {
    title: "Profile Settings",
    path: "/notary/settings",
    icon: (
      <SvgIcon fontSize="small">
        <SettingsIcon />
      </SvgIcon>
    ),
  },
];

/**
 * Main application menu items
 */
export const items = [
  {
    title: "Dashboard",
    path: "/dashboard",
    icon: (
      <SvgIcon fontSize="small">
        <BarChartIcon />
      </SvgIcon>
    ),
  },
  {
    title: "Select Counties",
    path: "/selectcounties",
    icon: (
      <SvgIcon fontSize="small">
        <PublicIcon />
      </SvgIcon>
    ),
  },
  {
    title: "Permits Hub",
    path: "",
    icon: (
      <SvgIcon fontSize="small">
        <FactCheckIcon />
      </SvgIcon>
    ),
    children: [
      {
        title: "Create Permit Package",
        path: "/permits/create",
        icon: (
          <SvgIcon fontSize="small">
            <PictureAsPdfIcon />
          </SvgIcon>
        ),
      },
      {
        title: "My Permit Packages",
        path: "/permits/my-permits",
        icon: (
          <SvgIcon fontSize="small">
            <InsertDriveFileIcon />
          </SvgIcon>
        ),
      },
      {
        title: "Notarization Requests",
        path: "/permits/invitations",
        icon: (
          <SvgIcon fontSize="small">
            <InsertDriveFileIcon />
          </SvgIcon>
        ),
      },
    ],
  },
  {
    title: "Custom Documents",
    path: "",
    icon: (
      <SvgIcon fontSize="small">
        <MdUploadFile />
      </SvgIcon>
    ),
    children: [
      {
        title: "Notarize/Record NOC",
        path: "/custom-documents/noc",
        icon: (
          <SvgIcon fontSize="small">
            <PiStampLight />
          </SvgIcon>
        ),
      },
    ],
  },
  {
    title: "Preset Configurations",
    path: "/defaults",
    icon: (
      <SvgIcon fontSize="small">
        <DriveFileRenameOutlineOutlinedIcon />
      </SvgIcon>
    ),
  },
  {
    title: "Payment",
    path: "/payment",
    icon: (
      <SvgIcon fontSize="small">
        <PaymentIcon />
      </SvgIcon>
    ),
  },
  {
    title: "Users",
    path: "/subusers",
    icon: (
      <SvgIcon fontSize="small">
        <GroupIcon />
      </SvgIcon>
    ),
  },
  {
    title: "Manage Subscription",
    path: "/subscription",
    icon: (
      <SvgIcon fontSize="small">
        <SubscriptionsIcon />
      </SvgIcon>
    ),
  },
  {
    title: "Profile Settings",
    path: "/profilesettings",
    icon: (
      <SvgIcon fontSize="small">
        <PersonSearchOutlinedIcon />
      </SvgIcon>
    ),
  },
  {
    title: "Licenses",
    path: "/contractors",
    icon: (
      <SvgIcon fontSize="small">
        <EngineeringIcon />
      </SvgIcon>
    ),
  },
];

export const getUserItems = (
  isEnterprise = false,
  isBranch = false,
  isAdmin = false,
  includeCustomersPackages = false,
) => {
  const baseItems = [...items];

  if (isAdmin || includeCustomersPackages) {
    return baseItems.map((item) => {
      if (item?.title === "Permits Hub") {
        const children = [...(item?.children || [])];
        const myPermitsIndex = children.findIndex(
          (child) => child.path === "/permits/my-permits",
        );

        const customersPackagesItem = {
          title: "Customers Packages",
          path: "/permits/customers-packages",
          icon: (
            <SvgIcon fontSize="small">
              <InsertDriveFileIcon />
            </SvgIcon>
          ),
        };

        const alreadyExists = children.some(
          (child) => child.path === customersPackagesItem.path,
        );

        if (!alreadyExists) {
          if (myPermitsIndex === -1) {
            children.push(customersPackagesItem);
          } else {
            children.splice(myPermitsIndex + 1, 0, customersPackagesItem);
          }
        }

        return { ...item, children };
      }

      return item;
    });
  }

  if (isEnterprise) {
    const usersIndex = baseItems.findIndex((item) => item.path === "/subusers");

    const branchesItem = {
      title: "Branches",
      path: "/branches",
      icon: (
        <SvgIcon fontSize="small">
          <BusinessIcon />
        </SvgIcon>
      ),
    };

    if (usersIndex === -1) {
      return [...baseItems, branchesItem];
    }

    baseItems.splice(usersIndex + 1, 0, branchesItem);
    return baseItems;
  } else if (isBranch) {
    const branchItem = {
      title: "All Permit Packages",
      path: "/permits/subuser-permits",
      icon: (
        <SvgIcon fontSize="small">
          <InsertDriveFileIcon />
        </SvgIcon>
      ),
    };

    return baseItems.map((item) => {
      if (item?.title === "Permits Hub") {
        const children = [...item?.children];

        const usersIndex = children.findIndex(
          (item) => item.path === "/permits/my-permits",
        );
        children.splice(usersIndex + 1, 0, branchItem);

        return { ...item, children };
      } else {
        return item;
      }
    });
  } else {
    return baseItems;
  }
};

/**
 * Build the Private Provider items based on domain.
 * If on permitrockstar domain, only show Managed Contractors.
 * Otherwise show the full Private Provider menu.
 */
export const getPrivateProviderItems = () => {
  if (isPermitRockstarDomain()) {
    // On permitrockstar domain, only show Managed Contractors
    return [
      {
        title: "Managed Contractors",
        path: "/privateProvider/contractors",
        icon: (
          <SvgIcon fontSize="small">
            <EngineeringIcon />
          </SvgIcon>
        ),
      },
    ];
  }

  // Other domains: full private provider menu
  return [
    {
      title: "Managed Contractors",
      path: "/privateProvider/contractors",
      icon: (
        <SvgIcon fontSize="small">
          <EngineeringIcon />
        </SvgIcon>
      ),
    },
    {
      title: "Private Provider",
      path: "",
      icon: (
        <SvgIcon fontSize="small">
          <BusinessIcon />
        </SvgIcon>
      ),
      children: [
        {
          title: "Dashboard",
          path: "/pp",
          icon: (
            <SvgIcon fontSize="small">
              <BarChartIcon />
            </SvgIcon>
          ),
        },
        {
          title: "Plan Review",
          path: "",
          icon: (
            <SvgIcon fontSize="small">
              <FactCheckIcon />
            </SvgIcon>
          ),
          children: [
            {
              title: "Submissions",
              path: "/pp/submissions",
              icon: (
                <SvgIcon fontSize="small">
                  <InsertDriveFileIcon />
                </SvgIcon>
              ),
            },
            {
              title: "New Submission",
              path: "/pp/submissions/new",
              icon: (
                <SvgIcon fontSize="small">
                  <PictureAsPdfIcon />
                </SvgIcon>
              ),
            },
            // {
            //   title: "Review Workbench",
            //   path: "/pp/workbench/:id",
            //   icon: (
            //     <SvgIcon fontSize="small">
            //       <FactCheckIcon />
            //     </SvgIcon>
            //   ),
            // },
            {
              title: "Submission Details",
              path: "/pp/submissions/:id",
              icon: (
                <SvgIcon fontSize="small">
                  <InsertDriveFileIcon />
                </SvgIcon>
              ),
            },
            {
              title: "Stamping Workflow",
              path: "/pp/submissions/:submissionId/stamp",
              icon: (
                <SvgIcon fontSize="small">
                  <FactCheckIcon />
                </SvgIcon>
              ),
            },
            {
              title: "Document Preview",
              path: "/pp/submissions/:submissionId/preview",
              icon: (
                <SvgIcon fontSize="small">
                  <InsertDriveFileIcon />
                </SvgIcon>
              ),
            },
          ],
        },
        {
          title: "Inspections",
          path: "",
          icon: (
            <SvgIcon fontSize="small">
              <BuildIcon />
            </SvgIcon>
          ),
          children: [
            {
              title: "Inspections Board",
              path: "/pp/inspections",
              icon: (
                <SvgIcon fontSize="small">
                  <ViewKanbanIcon />
                </SvgIcon>
              ),
            },
            {
              title: "Templates",
              path: "/pp/inspections/templates",
              icon: (
                <SvgIcon fontSize="small">
                  <ListAltIcon />
                </SvgIcon>
              ),
            },
            {
              title: "Inspection Details",
              path: "/pp/inspections/:permitId",
              icon: (
                <SvgIcon fontSize="small">
                  <BuildIcon />
                </SvgIcon>
              ),
            },
            {
              title: "Edit Inspection",
              path: "/pp/inspections/:permitId/edit",
              icon: (
                <SvgIcon fontSize="small">
                  <BuildIcon />
                </SvgIcon>
              ),
            },
          ],
        },
        {
          title: "Finance",
          path: "",
          icon: (
            <SvgIcon fontSize="small">
              <AttachMoneyIcon />
            </SvgIcon>
          ),
          children: [
            {
              title: "Dashboard",
              path: "/pp/finance",
              icon: (
                <SvgIcon fontSize="small">
                  <BarChartIcon />
                </SvgIcon>
              ),
            },
            {
              title: "Payouts",
              path: "/pp/finance/payouts",
              icon: (
                <SvgIcon fontSize="small">
                  <PaymentIcon />
                </SvgIcon>
              ),
            },
            {
              title: "Statements",
              path: "/pp/finance/statements",
              icon: (
                <SvgIcon fontSize="small">
                  <ReceiptIcon />
                </SvgIcon>
              ),
            },
          ],
        },
        {
          title: "Messaging",
          path: "",
          icon: (
            <SvgIcon fontSize="small">
              <ChatIcon />
            </SvgIcon>
          ),
          children: [
            {
              title: "Messages",
              path: "/pp/messaging",
              icon: (
                <SvgIcon fontSize="small">
                  <ChatIcon />
                </SvgIcon>
              ),
            },
            {
              title: "Message Thread",
              path: "/pp/messaging/:id",
              icon: (
                <SvgIcon fontSize="small">
                  <ChatIcon />
                </SvgIcon>
              ),
            },
          ],
        },
        {
          title: "Notifications",
          path: "/pp/notifications",
          icon: (
            <SvgIcon fontSize="small">
              <NotificationsIcon />
            </SvgIcon>
          ),
        },
        {
          title: "Photos & Media",
          path: "",
          icon: (
            <SvgIcon fontSize="small">
              <PhotoCameraIcon />
            </SvgIcon>
          ),
          children: [
            {
              title: "Photo Gallery",
              path: "/pp/photos",
              icon: (
                <SvgIcon fontSize="small">
                  <ImageIcon />
                </SvgIcon>
              ),
            },
            // {
            //   title: "Capture Photos",
            //   path: "/pp/photos/capture",
            //   icon: (
            //     <SvgIcon fontSize="small">
            //       <PhotoCameraIcon />
            //     </SvgIcon>
            //   ),
            // },
            {
              title: "Photo Requirements",
              path: "/pp/photos/requirements",
              icon: (
                <SvgIcon fontSize="small">
                  <GpsFixedIcon />
                </SvgIcon>
              ),
            },
          ],
        },
        {
          title: "Settings",
          path: "",
          icon: (
            <SvgIcon fontSize="small">
              <SettingsIcon />
            </SvgIcon>
          ),
          children: [
            {
              title: "General Settings",
              path: "/pp/settings",
              icon: (
                <SvgIcon fontSize="small">
                  <SettingsIcon />
                </SvgIcon>
              ),
            },
            {
              title: "City Reporting",
              path: "/pp/settings/city-reporting",
              icon: (
                <SvgIcon fontSize="small">
                  <SettingsIcon />
                </SvgIcon>
              ),
            },
            {
              title: "Digital Signatures",
              path: "/pp/settings/digital-signature",
              icon: (
                <SvgIcon fontSize="small">
                  <PiStampLight />
                </SvgIcon>
              ),
            },
          ],
        },
      ],
    },
  ];
};

/**
 * Backward-compatible constant (unchanged structure)
 */
const privateProviderItems = [
  {
    title: "Managed Contractors",
    path: "/privateProvider/contractors",
    icon: (
      <SvgIcon fontSize="small">
        <EngineeringIcon />
      </SvgIcon>
    ),
  },
  {
    title: "Private Provider",
    path: "",
    icon: (
      <SvgIcon fontSize="small">
        <BusinessIcon />
      </SvgIcon>
    ),
    children: [
      {
        title: "Dashboard",
        path: "/pp",
        icon: (
          <SvgIcon fontSize="small">
            <BarChartIcon />
          </SvgIcon>
        ),
      },
      {
        title: "Plan Review",
        path: "",
        icon: (
          <SvgIcon fontSize="small">
            <FactCheckIcon />
          </SvgIcon>
        ),
        children: [
          {
            title: "Submissions",
            path: "/pp/submissions",
            icon: (
              <SvgIcon fontSize="small">
                <InsertDriveFileIcon />
              </SvgIcon>
            ),
          },
          {
            title: "New Submission",
            path: "/pp/submissions/new",
            icon: (
              <SvgIcon fontSize="small">
                <PictureAsPdfIcon />
              </SvgIcon>
            ),
          },
          {
            title: "Review Workbench",
            path: "/pp/workbench/:id",
            icon: (
              <SvgIcon fontSize="small">
                <FactCheckIcon />
              </SvgIcon>
            ),
          },
          {
            title: "Submission Details",
            path: "/pp/submissions/:id",
            icon: (
              <SvgIcon fontSize="small">
                <InsertDriveFileIcon />
              </SvgIcon>
            ),
          },
          {
            title: "Stamping Workflow",
            path: "/pp/submissions/:submissionId/stamp",
            icon: (
              <SvgIcon fontSize="small">
                <FactCheckIcon />
              </SvgIcon>
            ),
          },
          {
            title: "Document Preview",
            path: "/pp/submissions/:submissionId/preview",
            icon: (
              <SvgIcon fontSize="small">
                <InsertDriveFileIcon />
              </SvgIcon>
            ),
          },
        ],
      },
      {
        title: "Inspections",
        path: "",
        icon: (
          <SvgIcon fontSize="small">
            <BuildIcon />
          </SvgIcon>
        ),
        children: [
          {
            title: "Inspection Board",
            path: "/pp/inspections",
            icon: (
              <SvgIcon fontSize="small">
                <ViewKanbanIcon />
              </SvgIcon>
            ),
          },
          {
            title: "Templates",
            path: "/pp/inspections/templates",
            icon: (
              <SvgIcon fontSize="small">
                <ListAltIcon />
              </SvgIcon>
            ),
          },
          {
            title: "Permit Inspections",
            path: "/pp/inspections/:permitId",
            icon: (
              <SvgIcon fontSize="small">
                <BuildIcon />
              </SvgIcon>
            ),
          },
        ],
      },
      {
        title: "Finance",
        path: "",
        icon: (
          <SvgIcon fontSize="small">
            <AttachMoneyIcon />
          </SvgIcon>
        ),
        children: [
          {
            title: "Dashboard",
            path: "/pp/finance",
            icon: (
              <SvgIcon fontSize="small">
                <BarChartIcon />
              </SvgIcon>
            ),
          },
          {
            title: "Payouts",
            path: "/pp/finance/payouts",
            icon: (
              <SvgIcon fontSize="small">
                <ReceiptIcon />
              </SvgIcon>
            ),
          },
          {
            title: "Statements",
            path: "/pp/finance/statements",
            icon: (
              <SvgIcon fontSize="small">
                <AssessmentIcon />
              </SvgIcon>
            ),
          },
        ],
      },
      {
        title: "Messaging",
        path: "/pp/messaging",
        icon: (
          <SvgIcon fontSize="small">
            <ChatIcon />
          </SvgIcon>
        ),
      },
      {
        title: "Notifications",
        path: "/pp/notifications",
        icon: (
          <SvgIcon fontSize="small">
            <NotificationsIcon />
          </SvgIcon>
        ),
      },
      {
        title: "Photos & Drawings",
        path: "",
        icon: (
          <SvgIcon fontSize="small">
            <PhotoCameraIcon />
          </SvgIcon>
        ),
        children: [
          {
            title: "Gallery",
            path: "/pp/photos",
            icon: (
              <SvgIcon fontSize="small">
                <ImageIcon />
              </SvgIcon>
            ),
          },
          {
            title: "Capture",
            path: "/pp/photos/capture",
            icon: (
              <SvgIcon fontSize="small">
                <PhotoCameraIcon />
              </SvgIcon>
            ),
          },
          {
            title: "Annotation",
            path: "/pp/photos/annotate",
            icon: (
              <SvgIcon fontSize="small">
                <EditIcon />
              </SvgIcon>
            ),
          },
          {
            title: "Requirements",
            path: "/pp/photos/requirements",
            icon: (
              <SvgIcon fontSize="small">
                <GpsFixedIcon />
              </SvgIcon>
            ),
          },
        ],
      },
      {
        title: "Settings",
        path: "",
        icon: (
          <SvgIcon fontSize="small">
            <SettingsIcon />
          </SvgIcon>
        ),
        children: [
          {
            title: "General",
            path: "/pp/settings",
            icon: (
              <SvgIcon fontSize="small">
                <SettingsIcon />
              </SvgIcon>
            ),
          },
          {
            title: "City Reporting",
            path: "/pp/settings/city-reporting",
            icon: (
              <SvgIcon fontSize="small">
                <AssessmentIcon />
              </SvgIcon>
            ),
          },
          {
            title: "Digital Signatures",
            path: "/pp/settings/digital-signature",
            icon: (
              <SvgIcon fontSize="small">
                <PiStampLight />
              </SvgIcon>
            ),
          },
        ],
      },
    ],
  },
];

/**
 * Admin menu items
 * Reports and Forms Management are omitted on permitrockstar domain
 * All admin items are now sub-items under the "Admin" parent
 */
const adminItems = [
  {
    title: "Admin",
    path: "",
    icon: (
      <SvgIcon fontSize="small">
        <AdminPanelSettingsIcon />
      </SvgIcon>
    ),
    children: [
      {
        title: "Overview",
        path: "/admin/overview",
        icon: (
          <SvgIcon fontSize="small">
            <HeroChartBarIcon />
          </SvgIcon>
        ),
      },
      {
        title: "Session Usage",
        path: "/admin/session-usage",
        icon: (
          <SvgIcon fontSize="small">
            <BarChartIcon />
          </SvgIcon>
        ),
      },
      {
        title: "Customers",
        path: "/admin/customers",
        icon: (
          <SvgIcon fontSize="small">
            <PeopleIcon />
          </SvgIcon>
        ),
      },
      {
        title: "Sales",
        path: "/admin/sales",
        icon: (
          <SvgIcon fontSize="small">
            <PointOfSaleIcon />
          </SvgIcon>
        ),
      },
      {
        title: "Notarization Management",
        path: "/admin/notaries",
        icon: (
          <SvgIcon fontSize="small">
            <BadgeIcon />
          </SvgIcon>
        ),
      },
      {
        title: "NOC Management",
        path: "/admin/tasks",
        icon: (
          <SvgIcon fontSize="small">
            <AssignmentTurnedIn />
          </SvgIcon>
        ),
      },
      {
        title: "DBPR Licenses",
        path: "/admin/licenses",
        icon: (
          <SvgIcon fontSize="small">
            <DescriptionIcon />
          </SvgIcon>
        ),
      },
      {
        title: "Configurations",
        path: "/admin/management",
        icon: (
          <SvgIcon fontSize="small">
            <BusinessIcon />
          </SvgIcon>
        ),
      },
      {
        title: "Email",
        path: "/admin/email",
        icon: (
          <SvgIcon fontSize="small">
            <EmailIcon />
          </SvgIcon>
        ),
      },
      // {
      //   title: "PDF Form Audit",
      //   path: "/admin/form-audit",
      //   icon: (
      //     <SvgIcon fontSize="small">
      //       <TextFieldsIcon />
      //     </SvgIcon>
      //   ),
      // },
      // Conditionally include Reports item based on domain
      ...(isPermitRockstarDomain()
        ? []
        : [
            {
              title: "Reports",
              path: "/admin/reports",
              icon: (
                <SvgIcon fontSize="small">
                  <AssessmentIcon />
                </SvgIcon>
              ),
            },
            {
              title: "Forms Management",
              path: "/admin/forms-management",
              icon: (
                <SvgIcon fontSize="small">
                  <NoteAddIcon />
                </SvgIcon>
              ),
            },
            // I want to exclude this path -> /privateProvider/contractors
            // ...getPrivateProviderItems().filter(
            //   (item) => item.path !== "/privateProvider/contractors"
            // ),
            // {
            //   title: "PP Contractor",
            //   path: "/pp/contractor/permits",
            //   icon: (
            //     <SvgIcon fontSize="small">
            //       <NoteAddIcon />
            //     </SvgIcon>
            //   ),
            // },
          ]),
    ],
  },
];
export { privateProviderItems, adminItems };
